import { AbstractAuthEventService } from '../abstract-auth-event/abstract-auth-event.service';
import { Injectable, Injector } from '@angular/core';
import { RoleEnum, UserClaims } from '../../models';
import { WindowHelperService } from '@anecdotes/utils';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { FirebaseWrapperService } from '../firebase-wrapper/firebase-wrapper.service';
import { Persistence, User } from '@angular/fire/auth';
import { continuePathQueryParam, isSessionExpiredQueryParam } from '../../constants';
import { AuthNavigatorService } from '../auth-navigator/auth-navigator.service';

@Injectable()
export class AuthService {
  private authEventService: AbstractAuthEventService;
  private firebaseWrapper: FirebaseWrapperService;
  private authNavigatorService: AuthNavigatorService;
  private windowHelperService: WindowHelperService;
  private userCache: Observable<UserClaims>;

  constructor(injector: Injector) {
    this.authEventService = injector.get(AbstractAuthEventService);
    this.firebaseWrapper = injector.get(FirebaseWrapperService);
    this.authNavigatorService = injector.get(AuthNavigatorService);
    this.windowHelperService = injector.get(WindowHelperService);

    this.userCache = this.buildCache().pipe(map((user) => {
      if (!user) {
        return;
      }

      // TODO: AN-9388
      // We need to map exisiting Manager to Collaborator
      // So the platform can treat managers as collaborators
      // until BE is fully migrated
      if (user.role?.endsWith(RoleEnum.Manager)) {
        user.role = user.role.replace(RoleEnum.Manager, RoleEnum.Collaborator);
      }

      // TODO AN-9719
      // Fixes SCIM users that have dictionary (object) in "name" claim instead of a string
      if (typeof user.name !== 'string') {
        user.name = Object.values(user.name).reverse().join(' ');
      }

      return user;
    }));
  }

  authStateChanged(): Observable<User> {
    return this.firebaseWrapper.authStateChanged();
  }

  getUserAsync(): Promise<UserClaims> {
    return firstValueFrom(this.userCache);
  }

  getUser(): Observable<UserClaims> {
    return this.userCache.pipe(filter((user) => !!user));
  }

  isAuditorSimulationMode(): Observable<boolean> {
    return this.getUser().pipe(map((user) => user?.in_auditor_simulation));
  }

  getAccessToken(): Observable<string> {
    return this.firebaseWrapper.idToken();
  }

  getAccessTokenAsync(): Promise<string> {
    return firstValueFrom(this.getAccessToken().pipe(filter((accessToken) => !!accessToken)));
  }

  refreshToken(): Promise<string> {
    return this.firebaseWrapper.refreshTokenAsync();
  }

  getAuthState(): Observable<User> {
    return this.firebaseWrapper.authState();
  }

  async signInWithTokenAsync(token: string, persistence: Persistence = null): Promise<void> {
    await this.firebaseWrapper.signInWithToken(token, persistence);
    this.authEventService.trackLogin();
  }

  async signOutAsync({ trackEvent = true, isSessionTimeout = false, preserveCurrentUrl = false }): Promise<any> {
    await this.signOutWithoutRedirectAsync(trackEvent);

    this.authNavigatorService.redirectToSignIn({
      [isSessionExpiredQueryParam]: isSessionTimeout,
      [continuePathQueryParam]: preserveCurrentUrl ? this.windowHelperService.getCurrentUrl() : undefined
    });
  }

  async signOutWithoutRedirectAsync(trackEvent = true): Promise<any> {
    if (trackEvent) {
      this.authEventService.trackLogout();
      // It's added as tracklogout might not be tracked due to immidiate logout
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    await this.firebaseWrapper.signOut();
  }

  async isAuthenticatedAsync(): Promise<boolean> {
    return !!(await firstValueFrom(this.firebaseWrapper.authState()));
  }

  private buildCache(): Observable<UserClaims> {
    return new Observable<UserClaims>((subscriber) => {
      const subscription = this.firebaseWrapper
        .authStateChanged()
        .pipe(
          switchMap(() =>
            this.firebaseWrapper.authState().pipe(
              switchMap(async (authState) => {
                if (authState) {
                  const token = await authState.getIdTokenResult();
                  if (!token.claims.name) {
                    token.claims.name = `${token.claims.first_name} ${token.claims.last_name}`;
                  }
                  return token.claims as unknown as UserClaims;
                }
              }),
            ),
          ),
        )
        .subscribe((data) => subscriber.next(data));

      return () => subscription.unsubscribe();
    }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }
}
